import { useCallback } from "react";
import {
  FEATURE_ADDON_KEYS,
  isAddonUsageAtLimit,
  isFeatureAddonLimitError,
  periodicAddonUsageCount,
} from "../actions/featureAddons";
import {
  FEATURE_ADDON_COMPETITOR_ACTION,
  FEATURE_ADDON_FREE_TIER_ID,
  getDefaultAddonCatalog,
} from "../utils/featureAddonCatalog";
import useFeatureAddonPaywall from "./useFeatureAddonPaywall";

const isActionMissing = ({ status, action }) => {
  const actions = status?.effective?.actions;
  return Array.isArray(actions) && action && !actions.includes(action);
};

const usageKeyForAction = (action) => {
  if (action === FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE) return "favorites";
  if (action === FEATURE_ADDON_COMPETITOR_ACTION.GENERATE) return "generate";
  return "actions";
};

const isActionsMeterBlocked = ({
  status,
  censored = false,
  action = null,
  pendingCount = 0,
}) => {
  if (Boolean(censored) || isActionMissing({ status, action })) return true;
  const isFree = status?.effective?.tierId === FEATURE_ADDON_FREE_TIER_ID;
  if (isFree && action) {
    const usage = status?.agencyAddon?.usage;
    const key = usageKeyForAction(action);
    let used = periodicAddonUsageCount(status?.agencyAddon, key);
    if (
      key === "favorites" &&
      !(usage && Object.prototype.hasOwnProperty.call(usage, "favorites"))
    ) {
      used = periodicAddonUsageCount(status?.agencyAddon, "actions");
    }
    return used + (Number(pendingCount) || 0) >= 1;
  }
  const used =
    periodicAddonUsageCount(status?.agencyAddon, "actions") +
    (Number(pendingCount) || 0);
  return isAddonUsageAtLimit(status, used);
};

/**
 * In-flight favorite/generate consumes. Survives modal remounts during a cold start.
 * Released when the endpoint responds, so a second click cannot reuse the same node count.
 * Free tier counts each action on its own quota.
 */
const pendingCompetitorActions = {
  [FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE]: 0,
  [FEATURE_ADDON_COMPETITOR_ACTION.GENERATE]: 0,
};

const defaultCompetitorPlatforms = () =>
  getDefaultAddonCatalog(FEATURE_ADDON_KEYS.COMPETITORS)?.tiers?.find(
    (tier) => tier.id === FEATURE_ADDON_FREE_TIER_ID
  )?.platforms ?? ["meta"];

/**
 * Competitors addon gates: monthly action meter, platforms, and favorite/generate.
 * `allowedPlatforms` is null for ungated agencies (every platform stays visible).
 */
const useCompetitorsAddon = () => {
  const paywall = useFeatureAddonPaywall({
    addonKey: FEATURE_ADDON_KEYS.COMPETITORS,
  });

  const allowedPlatforms = paywall.eligible
    ? (paywall.status?.effective?.platforms ?? defaultCompetitorPlatforms())
    : null;
  const visibilityLimit = !paywall.eligible
    ? null
    : paywall.status
      ? (paywall.status.effective?.visibilityLimit ?? null)
      : 15;

  const releasePendingAction = useCallback(
    (action = FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE) => {
      const key =
        pendingCompetitorActions[action] != null
          ? action
          : FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE;
      pendingCompetitorActions[key] = Math.max(
        0,
        pendingCompetitorActions[key] - 1
      );
    },
    []
  );

  const reserveAction = useCallback(
    async ({ action, censored = false }) => {
      if (!paywall.eligible) return false;
      pendingCompetitorActions[action] += 1;
      try {
        const blocked = await paywall.blockWhen((status) => {
          const isFree =
            status?.effective?.tierId === FEATURE_ADDON_FREE_TIER_ID;
          const pendingCount = isFree
            ? pendingCompetitorActions[action] - 1
            : pendingCompetitorActions[
                FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE
              ] +
              pendingCompetitorActions[
                FEATURE_ADDON_COMPETITOR_ACTION.GENERATE
              ] -
              1;
          return isActionsMeterBlocked({
            status,
            censored,
            action,
            pendingCount,
          });
        });
        if (blocked) releasePendingAction(action);
        return blocked;
      } catch (error) {
        releasePendingAction(action);
        throw error;
      }
    },
    [paywall.blockWhen, paywall.eligible, releasePendingAction]
  );

  const blockIfUsageReached = useCallback(
    (options = {}) =>
      reserveAction({
        action: FEATURE_ADDON_COMPETITOR_ACTION.FAVORITE,
        censored: options.censored,
      }),
    [reserveAction]
  );

  const blockIfGenerateLocked = useCallback(
    (options = {}) =>
      reserveAction({
        action: FEATURE_ADDON_COMPETITOR_ACTION.GENERATE,
        censored: options.censored,
      }),
    [reserveAction]
  );

  const openPaywallFromLimitError = useCallback(
    (response) => {
      if (!isFeatureAddonLimitError(response)) return false;
      paywall.openPaywall();
      return true;
    },
    [paywall.openPaywall]
  );

  return {
    ...paywall,
    allowedPlatforms,
    visibilityLimit,
    blockIfUsageReached,
    blockIfGenerateLocked,
    releasePendingAction,
    openPaywallFromLimitError,
  };
};

export default useCompetitorsAddon;
